import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoUsernameSetup = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateUsername = (value) => {
    // Username must be 3-20 characters, alphanumeric and underscores only
    const regex = /^[a-zA-Z0-9_]{3,20}$/;
    return regex.test(value);
  };

  const handleUsernameChange = async (value) => {
    setUsername(value);
    setAvailable(null);
    setSuggestions([]);

    if (!validateUsername(value)) {
      return;
    }

    // Check availability
    setChecking(true);
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(
        `${API}/crypto/username/check?username=${value}`,
        {},
        config
      );
      
      setAvailable(res.data.available);
      if (!res.data.available && res.data.suggestions) {
        setSuggestions(res.data.suggestions);
      }
    } catch (error) {
      console.error('Error checking username:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!available) {
      toast.error('Please choose an available username');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/crypto/username/register`,
        {
          username,
          email: user.email,
          phone_number: phoneNumber || null
        },
        config
      );

      setSuccess(true);
      toast.success(`✅ Username @${username} registered successfully!`);
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/crypto/wallet');
      }, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register username');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Username Registered!
              </h2>
              <p className="text-gray-600 mb-6">
                Your username @{username} has been successfully registered.
              </p>
              <div className="p-4 bg-emerald-50 rounded-lg inline-block">
                <p className="text-3xl font-bold text-emerald-600">@{username}</p>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Redirecting to your wallet...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/wallet')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wallet
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Setup Your Username</CardTitle>
            <CardDescription>
              Choose a unique username that others can use to send you crypto easily
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Input */}
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                    @
                  </div>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    placeholder="yourname"
                    className="pl-8"
                    required
                  />
                  {checking && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Validation Messages */}
                {username && !validateUsername(username) && (
                  <p className="text-sm text-red-600">
                    Username must be 3-20 characters (letters, numbers, underscores only)
                  </p>
                )}
                
                {available === true && (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      @{username} is available!
                    </AlertDescription>
                  </Alert>
                )}
                
                {available === false && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      @{username} is already taken. Try one of these:
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Suggestions */}
                {suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {suggestions.map((suggestion, index) => (
                      <Button
                        key={index}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleUsernameChange(suggestion)}
                        className="text-sm"
                      >
                        @{suggestion}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Phone Number (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1234567890"
                />
                <p className="text-xs text-gray-500">
                  Add your phone so others can send you crypto via phone number (+1234567890 format)
                </p>
                {phoneNumber && !phoneNumber.match(/^\+?[1-9]\d{7,14}$/) && (
                  <p className="text-xs text-red-600">
                    Please use international format: +[country code][number]
                  </p>
                )}
              </div>

              {/* Info Box */}
              <Alert>
                <AlertDescription>
                  <ul className="text-sm space-y-1">
                    <li>• Your username will be visible to other users</li>
                    <li>• You can't change it later, so choose carefully</li>
                    <li>• Anyone can send you crypto using @{username || 'yourname'}</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={!available || submitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                size="lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  'Register Username'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CryptoUsernameSetup;
